import {Injectable} from '@angular/core';
import {PatternShift} from '@domain/calendar/pattern/pattern-shift';
import {AddPatternShiftDialogService} from '@presentation/components/calendar/add-pattern-shift-dialog/add-pattern-shift-dialog.service';
import {
  AssertionUtils,
  ColDefBuilderFactoryService,
  ScheduleItem,
  TimelineHeaderComponent,
  TimelineHeaderParams,
  TimelineRowCellRendererParams,
  TimelineRowComponent
} from '@vdw/angular-component-library';
import {ColDef, GridApi, ICellRendererParams, IRowNode, NewValueParams} from 'ag-grid-community';
import {Duration} from 'luxon';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {PatternShiftRowData} from './pattern/add/pattern-shift-row-data.interface';
import {SchedulePreviewRowData} from './schedule/add/schedule/schedule-preview-row-data.interface';

@Injectable()
export class CalendarTimelineService {
  public startTime: Duration;
  public totalHours: number;
  private readonly TIMELINE_COLUMN_MIN_WIDTH = 1152;

  public constructor(private readonly addPatternShiftDialogService: AddPatternShiftDialogService) {}

  public getColumnDef(
    colDefBuilderFactoryService: ColDefBuilderFactoryService,
    unSubscribeOnViewDestroy: Subject<boolean>,
    addShiftFunction?: (rowNode: IRowNode, shifts: PatternShift[]) => void,
    deleteShiftFunction?: (id: number | string) => void
  ): ColDef {
    this.startTime = Duration.fromObject({hours: 6});
    this.totalHours = 24;
    return colDefBuilderFactoryService
      .getBuilder()
      .withColIdAndField('scheduleItems')
      .withHeaderComponent(TimelineHeaderComponent, () => ({startTime: this.startTime, totalHours: this.totalHours}) as TimelineHeaderParams)
      .withHeaderClass('no-padding')
      .withCellRenderer(
        TimelineRowComponent,
        (params: ICellRendererParams) =>
          ({
            startTime: this.startTime,
            onRowButtonClicked: (rowNode: IRowNode) =>
              this.addShift(params.api, rowNode, unSubscribeOnViewDestroy).subscribe((shifts: PatternShift[]) =>
                AssertionUtils.isNullOrUndefined(addShiftFunction) ? {} : addShiftFunction(rowNode, shifts)
              ),
            onScheduleItemDeleted: (id: number | string) => (AssertionUtils.isNullOrUndefined(deleteShiftFunction) ? {} : deleteShiftFunction(id))
          }) as TimelineRowCellRendererParams
      )
      .withCellClass(['no-padding', 'border-none'])
      .withMinWidth(this.TIMELINE_COLUMN_MIN_WIDTH)
      .withResizable(false)
      .withoutFilter()
      .withOnCellValueChanged((event: NewValueParams) => {
        this.redrawNextRow(event);
        this.updateTotalHours(event.api.getGridOption('rowData'));
        event.api.refreshHeader();
      })
      .build();
  }

  public updateTotalHours(rowData: PatternShiftRowData[] | SchedulePreviewRowData[]): void {
    const endTime = rowData.reduce(
      (maxEndTime: Duration, {scheduleItems}: PatternShiftRowData | SchedulePreviewRowData) => {
        if (scheduleItems.length > 0) {
          const lastEndTime = scheduleItems[scheduleItems.length - 1].endTime;
          return lastEndTime > maxEndTime ? lastEndTime : maxEndTime;
        }
        return maxEndTime;
      },
      this.startTime.plus({days: 1})
    );
    this.totalHours = Math.ceil(endTime.minus(this.startTime).as('hours'));
  }

  public getScheduleItemFromShift(shift: PatternShift): ScheduleItem {
    return {
      id: shift.id,
      name: shift.shift.name,
      color: shift.shift.color,
      startTime: shift.startTime,
      endTime: shift.endTime,
      breaks: shift.breakTimes
    };
  }

  private addShift(gridApi: GridApi, rowNode: IRowNode, unSubscribeOnViewDestroy: Subject<boolean>): Observable<PatternShift[]> {
    const previousDayEndTime = gridApi
      .getDisplayedRowAtIndex(rowNode.rowIndex - 1)
      ?.data?.scheduleItems?.at(-1)
      ?.endTime?.minus({days: 1});
    const nextDayStartTime = gridApi.getDisplayedRowAtIndex(rowNode.rowIndex + 1)?.data?.scheduleItems?.[0]?.startTime?.plus({days: 1});

    return this.addPatternShiftDialogService.openContentSwitcherDialog({startTime: this.startTime, rowData: rowNode.data, previousDayEndTime, nextDayStartTime}).pipe(
      tap((shifts: PatternShift[]) => {
        if (!AssertionUtils.isNullOrUndefined(shifts)) {
          const scheduleItems = [...rowNode.data.scheduleItems];
          const insertIndex = scheduleItems.filter((scheduleItem: ScheduleItem) => scheduleItem.startTime < shifts[0].startTime).length;
          scheduleItems.splice(insertIndex, 0, this.getScheduleItemFromShift(shifts[0]));
          rowNode.setDataValue('scheduleItems', scheduleItems);
        }
      }),
      takeUntil(unSubscribeOnViewDestroy)
    );
  }

  private redrawNextRow({api, node}: NewValueParams): void {
    const nextRow = api.getDisplayedRowAtIndex(node.rowIndex + 1);
    if (!AssertionUtils.isNullOrUndefined(nextRow)) {
      api.redrawRows({rowNodes: [nextRow]});
    }
  }
}
